import React from 'react';
import PropTypes from 'prop-types';
import { Transition } from 'react-transition-group';
import useForkRef from '../../hooks/useForkRef';
import createTransitions from '../../utils/createTransitions';

const styles = {
  entering: {
    opacity: 1,
  },
  entered: {
    opacity: 1,
  },
  exited: {
    opacity: 0,
  }
};

const reflow = (node) => node?.scrollTop;

function getTransitionProps(props, options) {
  const { timeout, easing, style = {} } = props;

  return {
    duration: style?.transitionDuration
      ?? (typeof timeout === 'number' ? timeout : timeout[options.mode] || 0),
    easing: style?.transitionTimingFunction
      ?? (typeof easing === 'object' ? easing[options.mode] : easing),
    delay: style?.transitionDelay,
  };
}

const TransitionFade = React.forwardRef((props, ref) => {

  const transitions = createTransitions();

  const defaultTimeout = {
    enter: transitions.duration.enteringScreen,
    exit: transitions.duration.leavingScreen,
  };

  const {
    addEndListener,
    appear = true,
    children,
    easing,
    in: inProp,
    onEnter,
    onEntered,
    onEntering,
    onExit,
    onExited,
    onExiting,
    style,
    timeout = defaultTimeout,
    transitionStyles,
    TransitionComponent = Transition,
    ...other
  } = props;

  const enableStrictModeCompat = true;
  const nodeRef = React.useRef(null);
  const handleRef = useForkRef(nodeRef, children?.ref, ref);

  const normalizedTransitionCallback = (callback) => (maybeIsAppearing) => {
    if (callback) {
      const node = nodeRef.current;

      if (maybeIsAppearing === undefined) {
        callback(node);
      } else {
        callback(node, maybeIsAppearing);
      }
    }
  };

  const handleEntering = normalizedTransitionCallback(onEntering);

  const handleEnter = normalizedTransitionCallback((node, isAppearing) => {
    if (node) {
      reflow(node); // So the animation always start from the start.

      const transitionProps = getTransitionProps(
        { style, timeout, easing },
        {
          mode: 'enter',
        },
      );

      node.style.webkitTransition = transitions.create('opacity', transitionProps);
      node.style.transition = transitions.create('opacity', transitionProps);
    }

    if (onEnter) {
      onEnter(node, isAppearing);
    }
  });

  const handleEntered = normalizedTransitionCallback(onEntered);

  const handleExiting = normalizedTransitionCallback(onExiting);

  const handleExit = normalizedTransitionCallback((node) => {
    if (node) {
      const transitionProps = getTransitionProps(
        { style, timeout, easing },
        {
          mode: 'exit',
        },
      );

      node.style.webkitTransition = transitions.create('opacity', transitionProps);
      node.style.transition = transitions.create('opacity', transitionProps);
    }

    if (onExit) {
      onExit(node);
    }
  });

  const handleExited = normalizedTransitionCallback(onExited);

  const handleAddEndListener = (next) => {
    if (addEndListener) {
      addEndListener(nodeRef.current, next);
    }
  };

  return (
    <TransitionComponent
      appear={appear}
      in={inProp}
      nodeRef={enableStrictModeCompat ? nodeRef : undefined}
      onEnter={handleEnter}
      onEntered={handleEntered}
      onEntering={handleEntering}
      onExit={handleExit}
      onExited={handleExited}
      onExiting={handleExiting}
      addEndListener={handleAddEndListener}
      timeout={timeout}
      {...other}
    >
      {(state, childProps) => (
        React.isValidElement(children) && React.cloneElement(children, {
          style: {
            opacity: 0,
            visibility: state === 'exited' && !inProp ? 'hidden' : undefined,
            ...style,
            ...styles?.[state],
            ...(transitionStyles?.[state] || {}),
            ...children.props?.style,
          },
          ref: handleRef,
          ...childProps,
        })
      )}
    </TransitionComponent>
  );
});

TransitionFade.displayName = 'TransitionFade';

TransitionFade.propTypes = {
  addEndListener: PropTypes.func,
  appear: PropTypes.bool,
  children: PropTypes.node,
  easing: PropTypes.oneOfType([
    PropTypes.shape({
      enter: PropTypes.string,
      exit: PropTypes.string,
    }),
    PropTypes.string,
  ]),
  in: PropTypes.bool,
  onEnter: PropTypes.func,
  onEntered: PropTypes.func,
  onEntering: PropTypes.func,
  onExit: PropTypes.func,
  onExited: PropTypes.func,
  onExiting: PropTypes.func,
  style: PropTypes.object,
  timeout: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      appear: PropTypes.number,
      enter: PropTypes.number,
      exit: PropTypes.number,
    }),
  ]),
  transitionStyles: PropTypes.object,
  TransitionComponent: PropTypes.node
};

export { TransitionFade };